import { useEffect, useRef } from 'react'

import type { ItemsWithPagination } from '@inno-sdks/files'
import { useQueryClient } from '@tanstack/react-query'
import { useAvailableApps } from 'modules/app-registry/hooks/useAvailableApps'
import { useUserContext } from 'shared/hooks/useUserContext'
import { logger } from 'shared/services/logger'

import { initFilesSdk } from '../api/filesSdk'
import { useAppFolderId } from '../api/hooks/useAppFolderId'
import { FILES_LIST_QUERY_KEY } from '../api/hooks/useFilesList'
import {
  DefaultShortcutConfig,
  getShortcutsForUser
} from '../constants/defaultShortcuts'
import { getLegacyDefaultShortcutIds } from '../constants/legacyDefaultShortcuts'
import { hasShortcutWithUrl } from '../utils/hasShortcutWithUrl'
import { provisionDesktopShortcut } from '../utils/provisionDesktopShortcut'

/**
 * Provisions the configured default shortcuts (core ones plus any role-gated
 * ones the user qualifies for) once the session's app folder id is known.
 *
 * - One `createItems` call per shortcut, awaited sequentially so the backend
 *   `createdAt` timestamps preserve the configured order (there is no explicit
 *   order field; the desktop sorts by `createdAt` ascending). A single failure
 *   is caught so it never blocks the remaining shortcuts.
 * - Removes the four legacy app-id-named shortcuts if they are present. They
 *   are excluded from the URL check so a legacy item cannot suppress creation
 *   of the correct human-readable shortcut.
 * - Idempotent: the current Files API response is the source of truth.
 *   Shortcuts whose URLs are already present are skipped; missing shortcuts
 *   are provisioned and the list is invalidated after a change.
 */
export const useProvisionDefaultShortcuts = (
  rootFolderItems?: ItemsWithPagination
) => {
  const { user } = useUserContext()
  const { data: appFolderId } = useAppFolderId()
  const { apps: availableApps, isLoading: isLoadingAvailableApps } =
    useAvailableApps()
  const queryClient = useQueryClient()
  const isProvisioningRef = useRef(false)

  useEffect(() => {
    if (
      appFolderId == null ||
      rootFolderItems == null ||
      isProvisioningRef.current
    )
      return

    if (isLoadingAvailableApps) return

    // The user's roles arrive under `roles` from the check-token/getUser flow
    // but under `user_roles` from the login response — fall back so either
    // auth path is handled.
    const legacyShortcutIds = getLegacyDefaultShortcutIds(rootFolderItems)
    const legacyShortcutIdSet = new Set(legacyShortcutIds)
    const rootItemsWithoutLegacyShortcuts = legacyShortcutIds.length
      ? {
          ...rootFolderItems,
          data: rootFolderItems.data.filter(
            item => !legacyShortcutIdSet.has(item.id)
          )
        }
      : rootFolderItems

    const shortcuts = getShortcutsForUser(
      user?.roles ?? user?.user_roles,
      availableApps
    ).filter(
      shortcut =>
        !hasShortcutWithUrl(
          rootItemsWithoutLegacyShortcuts,
          `${window.location.origin}${shortcut.path}`
        )
    )
    if (shortcuts.length === 0 && legacyShortcutIds.length === 0) return

    const { filesSdk, success } = initFilesSdk()
    if (!success || !filesSdk) return

    isProvisioningRef.current = true

    const removeLegacyShortcuts = async () => {
      if (legacyShortcutIds.length === 0) return false

      try {
        await filesSdk.items.trash(legacyShortcutIds)
        return true
      } catch (error) {
        logger.exception(
          'desktop.legacy_default_shortcut_cleanup_failed',
          error,
          { shortcutIds: legacyShortcutIds, appFolderId },
          { category: 'business' }
        )
        return false
      }
    }

    const provisionShortcut = async ({
      appId,
      name,
      path
    }: DefaultShortcutConfig) =>
      provisionDesktopShortcut({
        filesSdk,
        appFolderId,
        shortcutKey: appId,
        name,
        shortcutUrl: `${window.location.origin}${path}`,
        failureEvent: 'desktop.default_shortcut_provision_failed'
      })

    const provisionInOrder = async () => {
      let changedAny = await removeLegacyShortcuts()
      // Sequential (not parallel): each await ensures the next shortcut gets a
      // strictly later `createdAt`, keeping the configured order on the desktop.
      for (const shortcut of shortcuts) {
        // eslint-disable-next-line no-await-in-loop -- sequential by design (ordered createdAt)
        const result = await provisionShortcut(shortcut)
        changedAny = result === 'created' || changedAny
      }
      isProvisioningRef.current = false
      if (changedAny) {
        queryClient.invalidateQueries({ queryKey: [FILES_LIST_QUERY_KEY] })
      }
    }

    provisionInOrder()
  }, [
    appFolderId,
    availableApps,
    isLoadingAvailableApps,
    rootFolderItems,
    user?.roles,
    user?.user_roles,
    queryClient
  ])
}
